
import React from "react";
import { Link } from "react-router-dom";
import { SEOHead } from "@/components/SEOHead";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { Navigation } from "@/components/Navigation";
import { Pricing } from "@/components/landing/Pricing";
import { Footer } from "@/components/landing/Footer";
import { ChevronRight } from "lucide-react";

const PricingPage: React.FC = () => {
  const { t, language } = useLanguage();
  const { getPath } = useLanguagePath();

  // Dados para o schema.org
  const schemaData = {
    offers: [
      {
        "@type": "Offer",
        name: language === 'pt' ? "Plano Profissional" : "Professional Plan",
        price: "9.99",
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock"
      },
      {
        "@type": "Offer",
        name: language === 'pt' ? "Plano Premium" : "Premium Plan",
        price: "19.99",
        priceCurrency: "EUR",
        availability: "https://schema.org/InStock"
      }
    ],
    description: language === 'pt' 
      ? "Conheça os planos e preços da plataforma TrainerFOLIO para Personal Trainers."
      : "Learn about TrainerFOLIO platform plans and pricing for Personal Trainers.",
    url: `https://trainerfolio.com${getPath('/pricing')}`,
    seller: {
      "@type": "Organization",
      name: "TrainerFOLIO",
      url: "https://trainerfolio.com"
    }
  };

  const faqItems = language === 'pt' ? [
    {
      question: "Posso trocar de plano depois?",
      answer: "Sim, você pode atualizar ou fazer downgrade do seu plano a qualquer momento."
    },
    {
      question: "Existe período mínimo de contrato?",
      answer: "Não, todos os planos são cobrados mensalmente e você pode cancelar a qualquer momento."
    },
    {
      question: "Quais métodos de pagamento são aceitos?",
      answer: "Aceitamos cartões de crédito, débito e PayPal."
    },
    {
      question: "Existem devoluções das mensalidades ou anuidades?",
      answer: "Não são feitas devoluções das mensalidades ou anuidades. Disponibilizamos um período experimental gratuito de 14 dias para poderes testar o serviço sem necessidade de cartão de crédito. Caso não querias continuar a usar não necessitas fazer nada."
    },
    {
      question: "Como posso cancelar a minha subscrição?",
      answer: "Para cancelares a subscrição, basta ires a planos na aplicação, clicar em \"Gerir assinatura\" e efetuar o cancelamento."
    },
    {
      question: "Como posso renovar a minha subscrição?",
      answer: "Para renovares, apenas precisas de garantir que o teu cartão está válido. Assim que o primeiro pagamento for efetuado, as cobranças seguintes serão feitas automaticamente. Caso o pagamento falhe, a tua subscrição será cancelada e a conta ficará inativa."
    },
    {
      question: "Posso prescrever treinos utilizando o telemóvel?",
      answer: "Sim. Podes utilizar o software no telemóvel, tablet ou computador. No entanto, para uma experiência de utilização otimizada, recomendamos que o personal trainer utilize preferencialmente o computador."
    },
    {
      question: "É necessário instalar o software?",
      answer: "Não precisas obrigatoriamente de instalar o software, uma vez que é uma Progressive Web App. Tu e os teus alunos podem aceder diretamente através do navegador, sem necessidade de descarregar uma aplicação adicional que ocupe espaço nos dispositivos. No entanto, caso prefiras uma experiência semelhante a uma aplicação nativa, podes instalar facilmente através do Google Chrome no computador ou diretamente em dispositivos iOS e Android."
    }
  ] : [
    {
      question: "Can I change plans later?",
      answer: "Yes, you can upgrade or downgrade your plan at any time."
    },
    {
      question: "Is there a minimum contract period?",
      answer: "No, all plans are billed monthly and you can cancel at any time."
    },
    {
      question: "What payment methods are accepted?",
      answer: "We accept credit cards, debit cards, and PayPal."
    },
    {
      question: "Are there refunds for monthly or annual subscriptions?",
      answer: "No refunds are given for monthly or annual subscriptions. We provide a free 14-day trial period for you to test the service without requiring a credit card. If you don't want to continue using it, you don't need to do anything."
    },
    {
      question: "How can I cancel my subscription?",
      answer: "To cancel your subscription, simply go to Plans in the application, click on \"Manage subscription\" and complete the cancellation."
    },
    {
      question: "How do I renew my subscription?",
      answer: "To renew, you just need to ensure your card is valid. Once the first payment is made, subsequent charges will be made automatically. If the payment fails, your subscription will be canceled and the account will become inactive."
    },
    {
      question: "Can I prescribe workouts using a mobile phone?",
      answer: "Yes. You can use the software on your mobile phone, tablet or computer. However, for an optimized user experience, we recommend that the personal trainer preferably uses a computer."
    },
    {
      question: "Do I need to install the software?",
      answer: "You don't necessarily need to install the software, as it's a Progressive Web App. You and your students can access it directly through the browser, without the need to download an additional application that takes up space on your devices. However, if you prefer an experience similar to a native application, you can easily install it through Google Chrome on your computer or directly on iOS and Android devices."
    }
  ];

  return (
    <div className="flex flex-col min-h-screen">
      <SEOHead 
        title={language === 'pt' ? "TrainerFOLIO - Preços" : "TrainerFOLIO - Pricing"}
        description={language === 'pt' 
          ? "Conheça os planos e preços da plataforma TrainerFOLIO para Personal Trainers."
          : "Learn about TrainerFOLIO platform plans and pricing for Personal Trainers."}
        canonicalPath={getPath('/pricing')}
        schemaType="Offer"
        schemaData={schemaData}
      />

      <header>
        <Navigation />
      </header>

      <main className="flex-grow bg-gradient-to-b from-slate-50 to-white dark:from-gray-900 dark:to-gray-950">
        {/* Breadcrumbs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 mt-20">
          <nav className="flex items-center text-sm text-slate-500 dark:text-slate-400">
            <Link to={getPath('/')} className="hover:text-flame">
              {language === 'pt' ? 'Início' : 'Home'}
            </Link>
            <ChevronRight className="h-4 w-4 mx-2" />
            <span className="font-medium text-slate-700 dark:text-slate-300">
              {language === 'pt' ? 'Preços' : 'Pricing'}
            </span>
          </nav>
        </div>

        {/* Page Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <h1 className="text-4xl font-bold text-black dark:text-white">
            {language === 'pt' ? 'Planos e Preços' : 'Plans and Pricing'}
          </h1>
          <p className="mt-4 text-xl text-slate-600 dark:text-slate-400 max-w-3xl">
            {language === 'pt' 
              ? 'Escolha o plano ideal para o seu negócio como Personal Trainer.'
              : 'Choose the ideal plan for your business as a Personal Trainer.'}
          </p>
        </div>

        {/* Pricing Section */}
        <Pricing />
        
        {/* FAQ Section */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <h2 className="text-3xl font-bold text-center text-black dark:text-white mb-12">
            {language === 'pt' ? 'Perguntas Frequentes' : 'Frequently Asked Questions'}
          </h2>
          
          <div className="space-y-6 max-w-3xl mx-auto">
            {faqItems.map((item, index) => (
              <div key={index} className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm">
                <h3 className="text-xl font-semibold mb-2 dark:text-white">
                  {item.question}
                </h3>
                <p className="text-slate-600 dark:text-slate-300">
                  {item.answer}
                </p>
              </div>
            ))}
          </div>
        </div>
      </main>

      <footer>
        <Footer />
      </footer>
    </div>
  );
};

export default PricingPage;
